import { useTranslation } from 'react-i18next'

import { performBuildingsMassAction } from '../../../../api'
import { EMPTY_OBJECT } from '../../../../constants/common'
import { QA_NAME_CONFIRMATION } from '../../../../constants/qa'
import {
  useMassActions,
  useNotification,
  usePerformMassAction,
} from '../../../../hooks'
import { stopPropagation } from '../../../../utils/events'
import { ConfirmationModal } from '../../../Confirmation'
import {
  BUILDINGS_ACTION_ARCHIVE,
  BUILDINGS_ACTION_ARCHIVE_MASS,
  BUILDINGS_ACTION_EDIT_BUILDINGS_GROUP,
  BUILDINGS_ACTION_EDIT_BUILDINGS_GROUP_MASS,
  BUILDINGS_ACTION_EDIT_MANAGERS,
  BUILDINGS_ACTION_EDIT_MANAGERS_MASS,
  BUILDINGS_ACTION_EDIT_PROPERTY_OWNERS,
  BUILDINGS_ACTION_EDIT_PROPERTY_OWNERS_MASS,
  BUILDINGS_ACTION_REMOVE,
  BUILDINGS_ACTION_REMOVE_FROM_GROUP,
  BUILDINGS_ACTION_REMOVE_FROM_GROUP_MASS,
  BUILDINGS_ACTION_REMOVE_MASS,
  BUILDINGS_ACTION_RESTORE,
  BUILDINGS_ACTION_RESTORE_MASS,
  BUILDINGS_MASS_ACTION_FIND_AND_REMOVE_COMMAND,
  BUILDINGS_MASS_ACTION_NAMES,
} from '../../Building.constants'
import {
  BuildingsEditBuildingsGroupActionModal,
  BuildingsEditManagersActionModal,
  BuildingsEditPropertyOwnersActionModal,
} from '../../BuildingActions'

import {
  getAsyncNotificationText,
  getItemActionTypes,
  getNotificationTitle,
} from './useBuildingsActions.utils'

export function useBuildingsActions({
  excludeIds,
  filterValues,
  pagination,
  performApi = performBuildingsMassAction,
  selectAllPages,
  selectedIds,
  sub,
  onCleanSelection,
  onCloseModal,
  onReload,
  onRemoveFromGroup,
  onSetModal,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText,
    getNotificationTitle,
  })

  function handleErrorAction() {
    onCloseModal()

    handleAddErrorNotification('Notification.BuildingMassActionErrorTitleKey')
  }

  function showConfirmModal(title, onOk, btnText) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          cancelText: t('Modal.BuildingCancelBtnKey'),
          submitText: btnText,
        }}
        description={title}
        qaName={QA_NAME_CONFIRMATION}
        onCancel={onCloseModal}
        onSubmit={onOk}
      />
    )
  }

  const { isPerformingMassAction, massAction } = usePerformMassAction({
    excludeIds,
    filterValues,
    getAsyncTaskId,
    massActionNames: BUILDINGS_MASS_ACTION_NAMES,
    pagination,
    performApi,
    selectAllPages,
    selectedIds,
    sub,
    onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
    onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
    onCleanSelection,
    onCloseModal,
    onErrorAction: handleErrorAction,
    onReload,
  })

  function getHandleSubmitActionWithType({ action, id }) {
    return ({ actionType, value }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        actionType,
        value,
      })
    }
  }

  function showEditBuildingsModal({ action, id, item = EMPTY_OBJECT }) {
    onSetModal(
      <BuildingsEditBuildingsGroupActionModal
        formFieldsProps={{
          actionTypes: Number.isFinite(id) ? getItemActionTypes() : undefined,
        }}
        itemAction={Number.isFinite(id)}
        items={item.building_groups}
        onClose={onCloseModal}
        onSubmit={getHandleSubmitActionWithType({ action, id })}
      />
    )
  }

  function showEditManagersModal({ action, id, users }) {
    onSetModal(
      <BuildingsEditManagersActionModal
        formFieldsProps={{
          actionTypes: Number.isFinite(id) ? getItemActionTypes() : undefined,
          id,
        }}
        itemAction={Number.isFinite(id)}
        users={users}
        onClose={onCloseModal}
        onSubmit={getHandleSubmitActionWithType({ action, id })}
      />
    )
  }

  function showEditPropertyOwnersModal({ action, id, users }) {
    onSetModal(
      <BuildingsEditPropertyOwnersActionModal
        formFieldsProps={{
          actionTypes: Number.isFinite(id) ? getItemActionTypes() : undefined,
          id,
        }}
        itemAction={Number.isFinite(id)}
        users={users}
        onClose={onCloseModal}
        onSubmit={getHandleSubmitActionWithType({ action, id })}
      />
    )
  }

  function setItemAction(action, item) {
    switch (action) {
      case BUILDINGS_ACTION_ARCHIVE: {
        const hasDwellers = !!item.inhabitant_count
        const hasFlats = !!item.flat_count

        if (hasDwellers || hasFlats) {
          onSetModal(
            <ConfirmationModal
              actionButtonsProps={{
                submitText: t('Modal.OkBtnKey'),
              }}
              description={
                hasDwellers && hasFlats
                  ? t('Modal.AttentionMessageArchiveDwellersAndFlats')
                  : t('Modal.AttentionMessageArchiveFlats')
              }
              onCancel={onCloseModal}
              onSubmit={onCloseModal}
            />
          )
        } else {
          showConfirmModal(
            t('Modal.BuildingArchiveConfirmationTextKey'),
            e => {
              stopPropagation(e)
              massAction({ action, ids: item.id })
            },
            t('Modal.BuildingArchiveBtnKey')
          )
        }

        break
      }
      case BUILDINGS_ACTION_REMOVE:
        showConfirmModal(
          t('Modal.BuildingRemoveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({
              action,
              actionType: {
                value: BUILDINGS_MASS_ACTION_FIND_AND_REMOVE_COMMAND,
              },
              ids: item.id,
            })
          },
          t('Modal.CommonRemoveBtnKey')
        )
        break
      case BUILDINGS_ACTION_RESTORE:
        showConfirmModal(
          t('Modal.BuildingRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id })
          },
          t('Modal.BuildingRestoreBtnKey')
        )
        break
      case BUILDINGS_ACTION_EDIT_BUILDINGS_GROUP:
        showEditBuildingsModal({
          action: BUILDINGS_ACTION_EDIT_BUILDINGS_GROUP,
          id: item.id,
          item,
        })
        break
      case BUILDINGS_ACTION_EDIT_MANAGERS:
        showEditManagersModal({
          action: BUILDINGS_ACTION_EDIT_MANAGERS,
          id: item.id,
          users: item.responsible_users,
        })
        break
      case BUILDINGS_ACTION_EDIT_PROPERTY_OWNERS:
        showEditPropertyOwnersModal({
          action: BUILDINGS_ACTION_EDIT_PROPERTY_OWNERS,
          id: item.id,
          users: item.property_owners,
        })
        break
      case BUILDINGS_ACTION_REMOVE_FROM_GROUP:
        onRemoveFromGroup?.({
          buildingIds: [item.id],
          onCleanSelection,
          onReload,
        })
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case BUILDINGS_ACTION_ARCHIVE_MASS:
        showConfirmModal(
          t('Modal.BuildingMassArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.BuildingArchiveBtnKey')
        )
        break
      case BUILDINGS_ACTION_REMOVE_MASS:
        showConfirmModal(
          t('Modal.BuildingMassRemoveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({
              action,
              actionType: {
                value: BUILDINGS_MASS_ACTION_FIND_AND_REMOVE_COMMAND,
              },
            })
          },
          t('Modal.CommonRemoveBtnKey')
        )
        break
      case BUILDINGS_ACTION_RESTORE_MASS:
        showConfirmModal(
          t('Modal.BuildingMassRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.BuildingRestoreBtnKey')
        )
        break
      case BUILDINGS_ACTION_EDIT_BUILDINGS_GROUP_MASS:
        showEditBuildingsModal({
          action: BUILDINGS_ACTION_EDIT_BUILDINGS_GROUP_MASS,
        })
        break
      case BUILDINGS_ACTION_EDIT_MANAGERS_MASS:
        showEditManagersModal({
          action: BUILDINGS_ACTION_EDIT_MANAGERS_MASS,
        })
        break
      case BUILDINGS_ACTION_EDIT_PROPERTY_OWNERS_MASS:
        showEditPropertyOwnersModal({
          action: BUILDINGS_ACTION_EDIT_PROPERTY_OWNERS_MASS,
        })
        break
      case BUILDINGS_ACTION_REMOVE_FROM_GROUP_MASS:
        onRemoveFromGroup?.({
          excludeIds,
          filterValues,
          selectAllPages,
          selectedIds,
          onCleanSelection,
          onReload,
        })
        break
      default:
        return null
    }
  }

  return { isPerformingMassAction, setItemAction, setMassAction }
}
